import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Wallet, ArrowRight, ShieldCheck, Clock, Check, X, Star, Sparkles } from "lucide-react";
import { Shell } from "../components/Shell";
import { TripCard } from "../components/TripCard";
import { LiveOffers } from "../components/LiveOffers";
import { TripOffers } from "../components/TripOffers";
import { api, eur } from "../lib/api";
import { loadGoal, DEFAULT_GOAL } from "../lib/goal";
import { useI18n, badgeKey } from "../i18n";

export default function Results() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const goal = loadGoal() || DEFAULT_GOAL;
  const budget = Number(goal.budget || 2500);

  // undefined = still pending for the active request; set once that request settles.
  const [ctrl, setCtrl] = useState(undefined);
  const [live, setLive] = useState(undefined);
  const reqId = useRef(0);

  const goalKey = useMemo(() => JSON.stringify({
    b: budget, g: goal.geography?.value, a: goal.adults, c: goal.children,
    d: goal.destination_code || goal.destination, mh: goal.max_travel_hours,
    n: goal.nights, dd: goal.departure_date, rd: goal.return_date, l: lang,
  }), [budget, goal.geography?.value, goal.adults, goal.children, goal.destination_code, goal.destination, goal.max_travel_hours, goal.nights, goal.departure_date, goal.return_date, lang]);

  useEffect(() => {
    const myId = ++reqId.current;
    const sid = `res-${myId}`;
    // New search lifecycle: clear prior state so nothing from an older goal lingers.
    setCtrl(undefined);
    setLive(undefined);
    console.info(`[results ${sid}] start`, { budget, geo: goal.geography?.value, adults: goal.adults, children: goal.children, dest: goal.destination_code || goal.destination });
    api.recommendations(goal)
      .then((d) => { if (reqId.current === myId) { console.info(`[results ${sid}] controlled done`, { trips: d?.trips?.length || 0, no_match: d?.no_match }); setCtrl(d); } })
      .catch(() => { if (reqId.current === myId) { console.info(`[results ${sid}] controlled error`); setCtrl({ trips: [], no_match: false }); } });
    api.searchTripsLive(goal)
      .then((d) => { if (reqId.current === myId) { console.info(`[results ${sid}] live done`, { mode: d?.mode, flights: d?.flights_recommended?.length || 0, trips: d?.trip_offers?.length || 0 }); setLive(d); } })
      .catch(() => { if (reqId.current === myId) { console.info(`[results ${sid}] live error`); setLive(null); } });
  }, [goalKey]); // eslint-disable-line

  const ctrlDone = ctrl !== undefined;
  const liveDone = live !== undefined;
  const hasLive = !!(live && live.flights_live && live.flights_recommended?.length);
  const hasLiveTrips = !!(live && Array.isArray(live.trip_offers) && live.trip_offers.length > 0);
  const liveFlightData = hasLive ? { mode: "live", recommended: live.flights_recommended, fetched_at: live.fetched_at } : null;
  const trips = ctrl?.trips || [];
  const hasCtrl = trips.length > 0;
  const settled = ctrlDone && liveDone;
  // No-match is only ever true once BOTH searches for the active goal have completed
  // and neither produced a valid result.
  const noMatch = settled && !hasCtrl && !hasLive;

  useEffect(() => {
    if (!settled) return;
    const state = hasLive && hasCtrl ? "mixed_success" : hasLive ? "live_success" : hasCtrl ? "controlled_success" : "no_match";
    console.info(`[results] final state`, { state });
  }, [settled, hasLive, hasCtrl]);

  const best = trips[0];
  const geoLabel = ctrl?.geography?.label;
  // The requested stay doesn't fit inside the stated flexible window — an invalid
  // objective, not "genuinely searched and found nothing". Distinct explanation, never
  // the generic geography no-match copy.
  const invalidWindow = !!(ctrl?.invalid_window || live?.invalid_window);

  // Proof metrics must come from the active search execution. Never invent totals.
  // V2 currently searches one coherent date candidate; if that changes later, the
  // backend-provided count will update this UI automatically.
  const proofItems = [];
  if (liveDone && live?.mode && live.mode !== "controlled") {
    if (Number.isFinite(live.date_candidates_searched) && live.date_candidates_searched > 0) {
      proofItems.push(t("results.proofDates", { n: live.date_candidates_searched }));
    }
    if (Number.isFinite(live.flight_offer_count)) {
      proofItems.push(t("results.proofFlights", { n: live.flight_offer_count }));
    }
    if (Array.isArray(live.trip_offers)) {
      proofItems.push(t("results.proofTrips", { n: live.trip_offers.length }));
    }
  } else if (ctrlDone && hasCtrl) {
    proofItems.push(t("results.proofRecommendations", { n: trips.length }));
  }

  return (
    <Shell active="plan">
      <div className="mb-5">
        <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.24em] text-slate-400 font-display">
          <span style={{ color: "var(--brand)" }}>{t("results.kicker")}</span>
        </div>
        <h1 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl tracking-tight">{t("results.title")}</h1>
        <p className="mt-2 text-slate-500 text-sm max-w-xl">{t("results.sub")} <span className="font-semibold text-slate-700">{t("results.subTotal")}</span>.</p>
      </div>

      <div className="dv-card p-4 mb-6 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "var(--brand-soft)" }}><Wallet size={17} color="var(--brand)" /></div>
          <div>
            <div className="text-[11px] text-slate-400">{t("results.wholeBudget")}</div>
            <div className="dv-total text-xl text-slate-900">{eur(budget)}</div>
          </div>
        </div>
        {best && (
          <div className="flex items-center gap-2 text-sm" data-testid="results-best-summary">
            {best.budget_left >= 0 ? (
              <span className="dv-pill px-3 py-1.5 text-xs" style={{ background: "var(--book-soft)", color: "var(--book-ink)" }}>
                <ShieldCheck size={13} /> {t("results.bestKeeps", { x: eur(best.budget_left) })}
              </span>
            ) : (
              <span className="dv-pill px-3 py-1.5 text-xs" style={{ background: "var(--wait-soft)", color: "var(--wait-ink)" }}>
                <ShieldCheck size={13} /> {t("common.overBudget", { x: eur(-best.budget_left) })}
              </span>
            )}
          </div>
        )}
        <div className="flex items-center gap-1.5 text-[11px] text-slate-400"><Clock size={12} /> {t("results.lastChecked")}</div>
      </div>

      {proofItems.length > 0 && (
        <div className="dv-card p-4 mb-6 flex items-center gap-x-3 gap-y-1.5 flex-wrap" data-testid="results-proof" style={{ background: "var(--ink)", border: "none" }}>
          <div className="flex items-center gap-2 text-white flex-wrap">
            <Sparkles size={16} color="#fff" />
            {proofItems.map((item, i) => (
              <React.Fragment key={item}>
                {i > 0 && <span className="text-white/40">·</span>}
                <span className="font-display font-bold text-sm">{item}</span>
              </React.Fragment>
            ))}
          </div>
          <span className="text-xs text-white/70">{t("results.proofSupport")}</span>
        </div>
      )}

      <LiveOffers data={liveFlightData} />
      <TripOffers data={live} goal={goal} />

      {!ctrlDone ? (
        <div className="grid md:grid-cols-3 gap-5" data-testid="results-loading">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="dv-skeleton h-96 rounded-2xl" />)}</div>
      ) : hasCtrl ? (
        <>
          {geoLabel && (
            <div className="mb-4 text-sm text-slate-500 flex items-center gap-2" data-testid="results-geo-note">
              <span className="dv-pill text-xs px-2.5 py-1" style={{ background: "var(--ink)", color: "#fff" }}>{geoLabel}</span>
              {t("results.geoNote", { n: trips.length })}
            </div>
          )}
          <div className="grid md:grid-cols-3 gap-5">
            {trips.map((tr, i) => <TripCard key={tr.id} trip={tr} rank={i} goal={goal} onSelect={(x) => navigate(`/trip/${x.id}`)} />)}
          </div>
          <Comparison trips={trips} t={t} navigate={navigate} />
        </>
      ) : !liveDone ? (
        // Controlled search is empty but the live search is still running — keep loading,
        // never render a premature no-match.
        <div className="grid md:grid-cols-3 gap-5" data-testid="results-loading">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="dv-skeleton h-96 rounded-2xl" />)}</div>
      ) : hasLiveTrips ? (
        null
      ) : hasLive ? (
        <div className="dv-card p-5 text-center" data-testid="results-live-only-note">
          <p className="text-sm text-slate-500 max-w-md mx-auto">{t("results.liveOnlyNote")}</p>
        </div>
      ) : invalidWindow ? (
        <div className="dv-card p-8 sm:p-10 text-center" data-testid="results-invalid-window">
          <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center" style={{ background: "var(--wait-soft)" }}><X size={26} color="var(--wait-ink)" /></div>
          <h2 className="mt-4 font-display font-bold text-2xl">{t("results.invalidWindowTitle")}</h2>
          <p className="mt-2 text-slate-500 text-sm max-w-md mx-auto">{t("results.invalidWindowBody", { n: goal.nights })}</p>
          <button onClick={() => navigate("/plan")} data-testid="invalid-window-adjust" className="dv-btn dv-btn-primary text-base px-6 py-3 mt-6">{t("results.adjustGoal")} <ArrowRight size={16} /></button>
        </div>
      ) : (
        <div className="dv-card p-8 sm:p-10 text-center" data-testid="results-no-match">
          <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center" style={{ background: "var(--wait-soft)" }}><X size={26} color="var(--wait-ink)" /></div>
          <h2 className="mt-4 font-display font-bold text-2xl">{t("results.noMatchTitle")}</h2>
          <p className="mt-2 text-slate-500 text-sm max-w-md mx-auto">{t("results.noMatchBody", { geo: geoLabel || t("results.thisArea") })}</p>
          <button onClick={() => navigate("/plan")} data-testid="no-match-adjust" className="dv-btn dv-btn-primary text-base px-6 py-3 mt-6">{t("results.adjustGoal")} <ArrowRight size={16} /></button>
        </div>
      )}

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-8 text-center">
        <p className="text-sm text-slate-500">{t("common.recommendsControl")}</p>
        <button onClick={() => navigate("/plan")} className="dv-btn dv-btn-ghost text-sm px-5 py-2.5 mt-3">{t("results.adjustGoal")} <ArrowRight size={15} /></button>
      </motion.div>
    </Shell>
  );
}

const Comparison = ({ trips, t, navigate }) => {
  const rows = [
    { key: "cTotal", render: (tr) => <span className="dv-total text-base text-slate-900">{eur(tr.total)}</span> },
    { key: "cBudget", render: (tr) => <span className="font-semibold" style={{ color: tr.budget_left >= 0 ? "var(--book-ink)" : "var(--alert)" }}>{eur(tr.budget_left)}</span> },
    { key: "cTravel", render: (tr) => <span className="font-mono-num">{tr.travel_hours}h</span> },
    { key: "cHotel", render: (tr) => <span className="inline-flex items-center gap-1 font-semibold">{tr.hotel_rating}<Star size={12} className="fill-current" style={{ color: "var(--wait)" }} /></span> },
    { key: "cBags", render: (tr) => tr.baggage_included
        ? <span className="inline-flex items-center gap-1 text-[13px]" style={{ color: "var(--book-ink)" }}><Check size={14} /> {t("results.included")}</span>
        : <span className="inline-flex items-center gap-1 text-[13px]" style={{ color: "var(--alert)" }}><X size={14} /> {t("results.notIncluded")}</span> },
    { key: "cTransfers", render: (tr) => <span className="font-mono-num">{eur(tr.components.transfers)}</span> },
    { key: "cReason", render: (tr) => {
        const bk = badgeKey(tr.badge);
        return bk ? <span className="dv-pill text-[10px] px-2 py-0.5" style={{ background: "var(--paper)", border: "1px solid var(--line)", color: "var(--ink-soft)" }}>{t(bk)}</span> : <span className="text-slate-400">—</span>;
      } },
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} className="dv-card mt-6 overflow-hidden" data-testid="comparison-table">
      <div className="p-5 pb-3">
        <div className="font-display font-bold text-lg">{t("results.compareTitle")}</div>
        <p className="text-sm text-slate-500 mt-0.5">{t("results.compareSub")}</p>
      </div>
      <div className="dv-scroll-x overflow-x-auto">
        <table className="w-full min-w-[520px] border-collapse">
          <thead>
            <tr>
              <th className="text-left p-3 pl-5 sticky left-0 bg-white z-10" style={{ borderBottom: "1px solid var(--line)" }} />
              {trips.map((tr, i) => (
                <th key={tr.id} className="p-3 text-center align-bottom" style={{ borderBottom: "1px solid var(--line)", background: i === 0 ? "var(--brand-soft)" : "#fff" }}>
                  <div className="font-display font-bold text-sm text-slate-900">{tr.destination}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td className="text-left p-3 pl-5 text-xs font-semibold text-slate-500 sticky left-0 bg-white z-10 whitespace-nowrap" style={{ borderBottom: "1px solid var(--line)" }}>{t(`results.${r.key}`)}</td>
                {trips.map((tr, i) => (
                  <td key={tr.id} className="p-3 text-center text-sm text-slate-700 whitespace-nowrap" style={{ borderBottom: "1px solid var(--line)", background: i === 0 ? "rgba(37,99,235,0.04)" : "#fff" }}>
                    {r.render(tr)}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td className="sticky left-0 bg-white z-10 p-3" />
              {trips.map((tr, i) => (
                <td key={tr.id} className="p-3 text-center" style={{ background: i === 0 ? "rgba(37,99,235,0.04)" : "#fff" }}>
                  <button onClick={() => navigate(`/trip/${tr.id}`)} data-testid={`compare-choose-${tr.id}`} className="dv-btn dv-btn-ghost text-xs px-3 py-1.5">{t("results.seeDecision")}</button>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </motion.div>
  );
};
